// @flow
import React from 'react';

import notification from 'antd/lib/notification';
import { connect } from 'react-redux';
import { FormikProps } from 'formik';
import Input from 'antd/lib/input/Input';
import styled from 'styled-components';
import Button from 'antd/lib/button/button';
import DatePicker from 'antd/lib/date-picker';
import moment from 'moment';

import { Header, Section } from '../../../components/layout';
import {
  cleanVehicle,
  fetchVehicle,
  updateVehicle
} from '../../../ducks/vehicle';
import type {UserAccess, Vehicle} from '../../../lib/types';
import {accessTypeEnum, vehicleInventoryStatuses} from '../../../lib/enum';
import type { AppState } from '../../../ducks/redux';
import Grid, { GridItem } from '../../../components/layout/Grid';
import Form from '../../../components/Form';
import { formatDateTimeToISOString, navigate } from './../../../lib/helpers';
import { getDisabledDate } from './../lib';
import { notificationLoading } from './../../../components/Notifications';
import CancelButton from '../../../components/CancelButton';

const StyledSection = styled(Section)`
  padding: 16px;
`;
const Footer = styled(Section)`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type Props = {
  vehicle: Vehicle,
  vehicleId: string,
  fetchVehicle: Function,
  cleanVehicle: Function,
  updateVehicle: Function,
  userAccess: UserAccess;
};

class Accountant extends React.Component<Props> {
  async componentDidMount() {
    const { vehicleId } = this.props;
    await this.props.cleanVehicle();
    try {
      await this.props.fetchVehicle(vehicleId);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    }
  }

  onSubmit = async (values: Vehicle) => {
    try {
      notificationLoading({
        message: 'Сохранение данных...',
        key: 'saving'
      });
      if (values.isLease) {
        values.initialCost = 0;
        values.residualValue = 0;
        values.residualValueUpdateDate = null;
        values.restoreValue = 0;
        values.accountingRegistrationDate = null;
        values.accountingDeregistrationDate = null;
      }
      await this.props.updateVehicle(values);
      notification.success({
        message: 'Успешно сохранено',
        description: 'Изменения успешно сохранены'
      });
      navigate(`/vehicles/${values.id}/documentation/accountant`);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('saving');
    }
  };

  handleCancel = () => {
    navigate(`/vehicles/${this.props.vehicleId}/documentation/accountant`);
  };

  formatDate = value => {
    return value ? moment.utc(value) : value;
  };

  render() {
    const { vehicle = {}, userAccess } = this.props;
    const isAdmin = userAccess.includes(accessTypeEnum.admin);
    return (
      <>
        <Header
          left={vehicle && <h1>Бухгалтерские данные {vehicle.licensePlate}</h1>}
        />
        <Form initialValues={vehicle || {}} onSubmit={this.onSubmit}>
          {(FormField, formikProps: FormikProps) => {
            const {
              handleSubmit,
              handleBlur,
              handleChange,
              setFieldValue,
              dirty
            } = formikProps;
            return (
              <form onSubmit={handleSubmit}>
                <StyledSection>
                  <Grid gutter="16px">
                    {vehicle && !vehicle.isLease && (
                      <>
                        <GridItem>
                          <FormField
                            label="Первоначальная стоимость, ₽ (Без НДС)"
                            name="initialCost"
                          >
                            {({ value, name }) => (
                              <Input
                                name={name}
                                value={value}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </FormField>
                        </GridItem>
                        <GridItem>
                          <FormField
                            label="Остаточная стоимость, ₽ (Без НДС)"
                            name="residualValue"
                          >
                            {({ value, name }) => (
                              <Input
                                disabled
                                name={name}
                                value={value}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </FormField>
                        </GridItem>
                        <GridItem>
                          <FormField
                            fast
                            label="Дата обновления остаточной стоимости"
                            name="residualValueUpdateDate"
                          >
                            {({ value, name }) => (
                              <DatePicker
                                disabled
                                format="DD.MM.YYYY"
                                value={value ? moment.utc(value) : value}
                                onChange={(value: Object, dateString: string) => {
                                  setFieldValue(
                                    name,
                                    formatDateTimeToISOString(value, dateString)
                                  );
                                }}
                                disabledDate={(currentDate: moment) =>
                                  getDisabledDate(currentDate)
                                }
                              />
                            )}
                          </FormField>
                        </GridItem>
                        <GridItem>
                          <FormField
                            label="Восстановительная стоимость, ₽ (Без НДС)"
                            name="restoreValue"
                          >
                            {field => <Input {...field} />}
                          </FormField>
                        </GridItem>
                        <GridItem>
                          <FormField
                            fast
                            label="Дата принятия к бухгалтерскому учету"
                            name="accountingRegistrationDate"
                          >
                            {({ value, name }) => (
                              <DatePicker
                                disabled
                                format="DD.MM.YYYY"
                                value={value ? moment.utc(value) : value}
                                onChange={(value: Object, dateString: string) => {
                                  setFieldValue(
                                    name,
                                    formatDateTimeToISOString(value, dateString)
                                  );
                                }}
                                disabledDate={(currentDate: moment) =>
                                  getDisabledDate(currentDate)
                                }
                              />
                            )}
                          </FormField>
                        </GridItem>
                        <GridItem>
                          <FormField
                            fast
                            label="Дата списания ТС с бухгалтерского учета"
                            name="accountingDeregistrationDate"
                          >
                            {({ value, name }) => (
                              <DatePicker
                                disabled
                                format="DD.MM.YYYY"
                                value={value ? moment.utc(value) : value}
                                onChange={(value: Object, dateString: string) => {
                                  setFieldValue(
                                    name,
                                    formatDateTimeToISOString(value, dateString)
                                  );
                                }}
                                disabledDate={(currentDate: moment) =>
                                  getDisabledDate(currentDate)
                                }
                              />
                            )}
                          </FormField>
                        </GridItem>
                      </>
                    )}
                    <GridItem>
                      <FormField
                        label="Rn инвентарной карточки"
                        name="inventoryCardParusId"
                      >
                        {field => <Input disabled={!isAdmin} {...field} />}
                      </FormField>
                    </GridItem>
                    <GridItem>
                      <FormField
                        label="Статус инвентарной карточки в ИС Парус"
                        name="inventoryStatus"
                      >
                        {({ value }) => (
                          <Input
                            disabled
                            value={vehicleInventoryStatuses[value]}
                          />
                        )}
                      </FormField>
                    </GridItem>
                  </Grid>
                </StyledSection>
                <Footer>
                  <Button type="primary" htmlType="submit">
                    Сохранить
                  </Button>
                  <CancelButton dirty={dirty} onClick={this.handleCancel}>
                    Отменить
                  </CancelButton>
                </Footer>
              </form>
            );
          }}
        </Form>
      </>
    );
  }
}

export default connect(
  (state: AppState, props: Props) => ({
    userAccess: state.auth.profile.access,
    vehicle: state.vehicle,
    vehicleId: parseInt(props.vehicleId, 10)
  }),
  {
    fetchVehicle,
    cleanVehicle,
    updateVehicle
  }
)(Accountant);
